/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import moment from 'moment';
import 'moment-timezone';
import numeral from 'numeral';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';

export const DATE_DATA_FORMAT = 'YYYY-MM-DD';
export const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';
export const DATE_HISTORICAL_START = '1992-01-01';
export const YEAR_DISPLAY_FORMAT = 'YYYY';
export const MILLIS = 1000;
export const SECONDS = 60;
export const MINUTES = 60;
export const HOURS = 24;
export const CALENDAR_MONTHS = 12;
export const CURRENT_YEAR = moment().year();
export const PRIOR_YEAR = moment().subtract(1, 'year').year();

// Required to set the default timezone to EST
moment.tz.setDefault('America/New_York');

// Timezones
export const TZ_UTC = 'UTC';
export const TZ_DEFAULT = 'DEFAULT';

export const today = (format) => {
  return moment.utc().format(format);
};

// TODO: Refactor to consider business days, holidays, etc.
export const isWeekend = (possibleWeekendDate = moment()) =>
  possibleWeekendDate.day() === 6 || possibleWeekendDate.day() === 0;
export const isInsideRefreshHours = (currentDateTime = moment()) => {
  return (
    currentDateTime.hour() < 1 ||
    currentDateTime.hour() >= 10 ||
    (currentDateTime.hour() === 9 && currentDateTime.minute() >= 45)
  );
};
export const isTimeToShowRefresh = (possibleRefreshDate = moment()) => {
  return (
    !isWeekend(possibleRefreshDate) && isInsideRefreshHours(possibleRefreshDate)
  );
};

export const mmmddyyyy = (value) => {
  return moment.utc(value).format('MMM D, YYYY');
};

export const mmddyyyy = (value, separator = '-', local = false) => {
  const format = `MM${separator}DD${separator}YYYY`;
  if (local) {
    return moment.utc(value).local().format(format);
  }
  return moment.utc(value).format(format);
};

export const yyyymmdd2mmmddyyyy = (value) => {
  return moment.utc(value, 'YYYY-MM-DD').format('MMM D, YYYY');
};

export const yyyymmdd = (value, tz = TZ_UTC) => {
  switch (tz) {
    case TZ_UTC:
      return moment.utc(value).format(DATE_DATA_FORMAT);
    case TZ_DEFAULT:
    default:
      return moment(value).format(DATE_DATA_FORMAT);
  }
};

export const yyyymmddPortfolioAsOfDate = (value, tz = TZ_UTC) => {
  if (!value) {
    return '-';
  }
  switch (tz) {
    case TZ_UTC:
      return moment.utc(value).format(DATE_DATA_FORMAT);
    case TZ_DEFAULT:
    default:
      return moment(value).format(DATE_DATA_FORMAT);
  }
};

export const dayDifference = (
  value,
  referenceDate = moment.utc(),
  local = false
) => {
  let day = moment.utc(value);
  if (local) {
    day = moment.utc(value).local();
  }
  return day.diff(moment.utc(referenceDate), 'days');
};

// need to determine if the selection is either a year or number of days
export const isYear = (selection) =>
  selection.startsWith(2) && selection.length === 4;

/**
 * A check if the given day is in between or equal to the from date and to date.
 * @param {string|moment} day
 * @param {string|moment} from
 * @param {string|moment} to
 * @returns {boolean}
 */
export const isBetweenFromAndToDates = (day, from, to) => {
  let d = day,
    f = from,
    t = to;
  if (typeof day === 'string') {
    d = moment.utc(yyyymmdd(day));
  }
  if (typeof from === 'string') {
    f = moment.utc(from);
  }
  if (typeof to === 'string') {
    t = moment.utc(to);
  }
  return d.isBetween(f, t, null, '[]');
};

/* Why the heck is this in dates TODO move to formats */
export const currency = (value) => {
  return typeof value !== 'number' || isNaN(value)
    ? '$0.00'
    : numeral(value).format('$0,0.00');
};

export const furthestDateFromReferenceDate = (
  collection,
  referenceDate = moment.utc()
) => {
  const differences = collection.map((obj) =>
    dayDifference(obj.date, referenceDate)
  );
  const furthestInThePast =
    isEmpty(differences) || isNaN(Math.min(...differences))
      ? -1095
      : Math.min(...differences);

  return yyyymmdd(moment.utc(referenceDate).add(furthestInThePast, 'days'));
};

export const isValidInputDate = (date) =>
  moment(date, 'MM/DD/YYYY', true).isValid() ||
  moment(date, 'MM-DD-YYYY', true).isValid() ||
  moment(date, 'YYYY-MM-DD', true).isValid();

export const isValidBeneficiaryDob = (date) =>
  /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date);

const DEFAULT_DEBOUNCE = 750;

export const useDebounce = (effect, deps = [], delay = DEFAULT_DEBOUNCE) => {
  useEffect(() => {
    const timeout = setTimeout(() => effect(), delay);

    return () => clearTimeout(timeout);

    // eslint doesn't like the spread op in the dependency array
    // but we need it to pass each dep through
  }, [...deps, delay]);
};

const nullCheck = (date1, date2) => {
  if (isEmpty(date1)) {
    return date2;
  }
  if (isEmpty(date2)) {
    return date1;
  }
};

export const minOfDates = (date1, date2) => {
  const defaultValue = nullCheck(date1, date2);
  if (defaultValue) {
    return defaultValue;
  }

  const _date1 = moment(date1);
  const _date2 = moment(date2);

  return _date1.isSameOrBefore(_date2) ? date1 : date2;
};

export const maxOfDates = (date1, date2) => {
  const defaultValue = nullCheck(date1, date2);
  if (defaultValue) {
    return defaultValue;
  }

  const _date1 = moment(date1);
  const _date2 = moment(date2);

  return _date1.isSameOrAfter(_date2) ? date1 : date2;
};

export const getMostRecentDate = (offset = 0) =>
  moment().subtract(offset, 'day').format(DATE_DATA_FORMAT);

export const xMonthsAgo = (startDate, x) =>
  moment(startDate).subtract(x, 'months').format(DATE_DATA_FORMAT);

export const startOfYear = () =>
  moment(getMostRecentDate()).startOf('year').format(DATE_DATA_FORMAT);

export const startOfPriorYear = () =>
  moment(getMostRecentDate())
    .subtract(1, 'year')
    .startOf('year')
    .format(DATE_DATA_FORMAT);

export const endOfPriorYear = () =>
  moment(getMostRecentDate(1))
    .subtract(1, 'year')
    .endOf('year')
    .format(DATE_DATA_FORMAT);

export const utcFormat = (d) =>
  new Date(d.replace(/-/g, '/').replace(/T.+/, ''));

//returns the string of the month
// 1 = January 12 = december
// the 15th exists for everymonth
export const getMonthByNumeral = (numeral) => {
  const newNumeral = parseInt(numeral) - 1;
  const getMonth = new Date();
  getMonth.setDate(15);
  getMonth.setMonth(newNumeral);
  return getMonth.toLocaleString('en-US', { month: 'long' });
};

export const differenceInDates = (date1, date2) => {
  //expecting something in new date() form

  const diffTime = Math.abs(date2 - date1);
  const minutes = diffTime * MILLIS;
  const days = Math.ceil(diffTime / (MILLIS * SECONDS * MINUTES * HOURS));
  const years = Math.ceil(date1.getFullYear() - date2.getFullYear());
  const months = Math.ceil(
    date1.getMonth() - date2.getMonth() + CALENDAR_MONTHS * years
  );
  return { minutes, days, months, years };
};

export const invalidDate = (toDate, fromDate, maxDate, minDate) => {
  return (yyyymmdd(toDate) > yyyymmdd(maxDate)) || (yyyymmdd(fromDate) < yyyymmdd(minDate));
}

export const convertToMMDDYYYY = (value) => {
  return moment.utc(value).format(DATE_DISPLAY_FORMAT);
}